import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Chip, IconButton, InputAdornment, Link, TextField, Typography } from "@mui/material";
import { LockOutlined, MailOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import { auth } from "../../firebase/firebaseConfig";
import { signInWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { authErrorText, backendLogin } from "../../firebase/authErrors";
import AuthLayout, { AuthTitle, SubmitLabel, fieldSx, helperTextSx, mutedTextSx, submitSx } from "../../components/AuthLayout";
import { colors } from "../../styles/tokens";

/** Sign-in for Vavetek super admins only, kept apart from the shared /login page. */
export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { severity, text, canResend }
  const [unverifiedUser, setUnverifiedUser] = useState(null);

  const successMessage = location.state?.message || "";

  useEffect(() => {
    if (localStorage.getItem("jwttoken")) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get("email");
    const password = data.get("password");

    setMessage(null);
    setBusy(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        setUnverifiedUser(user);
        setMessage({
          severity: "warning",
          text: "Please verify your email before logging in. Open the link we emailed you, then log in again.",
          canResend: true,
        });
        return;
      }

      const idToken = await user.getIdToken();
      const json = await backendLogin("/api/admin/adminuser/admin-login", idToken);
      localStorage.setItem("jwttoken", json.token || idToken);
      localStorage.setItem("user", json.displayName || user.displayName || "Anonymous");
      localStorage.setItem("email", json.email || user.email);
      navigate(`/admin/dashboard`);
    } catch (error) {
      let text = authErrorText(error);
      if (error.status === 401) {
        text = "This account isn't a Vavetek super admin. Customers and IT admins sign in on the main login page.";
      } else if (error.status) {
        text = "We couldn't sign you in. Please try again.";
      }
      setMessage(text ? { severity: "error", text } : null);
    } finally {
      setBusy(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedUser) return;
    try {
      await sendEmailVerification(unverifiedUser);
      setMessage({
        severity: "success",
        text: `Verification email sent to ${unverifiedUser.email}. Check your inbox, then log in.`,
      });
    } catch (error) {
      setMessage({ severity: "error", text: authErrorText(error) || "We couldn't send the email. Please try again." });
    }
  };

  return (
    <AuthLayout>
      <Chip
        label="SUPER ADMIN"
        size="small"
        sx={{ mb: 1.5, fontWeight: 600, letterSpacing: "1px", color: colors.goldText, backgroundColor: colors.beige }}
      />
      <AuthTitle lead="Vavetek" accent="admin console" />
      <Typography sx={{ ...mutedTextSx, mt: 2, mb: 3, fontSize: "15px" }}>
        For Vavetek staff only. Customers and company IT admins sign in on the{" "}
        <Link component={RouterLink} to="/login">
          main login page
        </Link>
        .
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {message && (
        <Alert
          severity={message.severity}
          sx={{ mb: 3, textAlign: "left" }}
          action={
            message.canResend && (
              <Button color="inherit" size="small" onClick={handleResendVerification}>
                Resend email
              </Button>
            )
          }
        >
          {message.text}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          name="email"
          type="email"
          placeholder="Email"
          autoComplete="email"
          autoFocus
          required
          sx={fieldSx}
          inputProps={{ "aria-label": "Email" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutline sx={{ color: colors.muted }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          margin="normal"
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          autoComplete="current-password"
          required
          sx={fieldSx}
          inputProps={{ "aria-label": "Password" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined sx={{ color: colors.muted }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <VisibilityOff sx={{ color: colors.muted }} /> : <Visibility sx={{ color: colors.muted }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box mt={1} display="flex" justifyContent="flex-end">
          <Link href="/forgotpassword" sx={helperTextSx}>
            Forgot password?
          </Link>
        </Box>

        <Box mt={3}>
          <Button type="submit" variant="contained" size="large" fullWidth disabled={busy} sx={submitSx}>
            <SubmitLabel busy={busy}>Log in</SubmitLabel>
          </Button>
        </Box>
      </Box>
    </AuthLayout>
  );
}
